import { useMutation, UseMutationOptions, useQueries, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Id, toast } from 'react-toastify'
import i18n from '../../i18n'
import { convertDateStringToTimeStamp } from '../../utils/general'
import { UpdateFileResponse } from '../globalTypes'
import { invalidateQueries, updateToast } from '../helpers'
import { deleteImages, getImageByUrl, getImagesById, syncImage, updateImages, uploadImages } from './functions'
import { handleImagesUpdateInvalidation } from './helpers'
import {
    DeleteImageParams,
    Image,
    QueryParams,
    SyncImageParams,
    UpdateImageParams,
    UploadImageContext,
    UploadImageParams,
    UploadImageResponse,
} from './types'

const images = {
    GetImages: {
        useQuery: (params: QueryParams, options?: Omit<UseQueryOptions<Image[], AxiosError>, 'queryKey' | 'queryFn'>) => {
            const { collection, module, parent } = params

            return useQuery({
                queryKey: [collection, module, parent],
                queryFn: () => getImagesById(parent, module, collection),
                staleTime: 1 * 60 * 1000,
                ...options,
            })
        },
    },
    UploadImages: {
        useMutation: (options?: UseMutationOptions<UploadImageResponse, AxiosError, UploadImageParams, UploadImageContext>) => {
            return useMutation({
                onMutate: (params) => {
                    const { collection, files } = params
                    const label = collection === 'ground_plan' ? 'groundPlan' : 'photo'
                    const toastId = toast.loading(i18n.t(`toast.loading.${label}`, { filesCount: files?.length }) as string)

                    return { toastId, label }
                },
                mutationFn: (params) => uploadImages(params),
                onSuccess: (data, params, context) => {
                    const { collection, module, entityData } = params
                    const toastId = context?.toastId
                    const label = context?.label
                    const filesCount = params.files.length

                    const successMessage = i18n.t(`toast.loading.${label}.success`, { successCount: filesCount, filesCount }) as string

                    toastId && updateToast(toastId, successMessage, 'success')

                    invalidateQueries([collection, module, entityData?.parent])

                    // TODO: fix module names
                    const moduleName = module === 'property' ? 'properties' : 'projects'

                    invalidateQueries([moduleName, 'list'])
                },
                onError: (error, params, context) => {
                    const filesCount = params.files.length
                    const errorMessage = i18n.t(`toast.loading.${context?.label}.error`, { errorCount: filesCount, filesCount }) as string

                    context?.toastId && updateToast(context?.toastId, errorMessage, 'error')
                    console.error(error)
                },
                ...options,
            })
        },
    },
    DeleteImages: {
        useMutation: (options?: UseMutationOptions<UpdateFileResponse[], AxiosError, DeleteImageParams>) => {
            return useMutation({
                mutationFn: (params) => deleteImages(params),
                onSuccess: (data, params) => {
                    const { collection, module, parent } = params
                    // TODO
                    const moduleName = module === 'property' ? 'properties' : 'projects'

                    invalidateQueries([collection, module, parent])
                    invalidateQueries([moduleName, 'list'])
                },
                onError: (error) => {
                    console.log('Images delete error:', error)
                },
                ...options,
            })
        },
    },
    UpdateImages: {
        useMutation: (options?: UseMutationOptions<UpdateFileResponse[], AxiosError, UpdateImageParams[], Id>) => {
            return useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.loading.images') as string)
                    return toastId
                },
                mutationFn: (params) => updateImages(params),
                onSuccess: (data, params, context) => {
                    const toastId = context
                    const isSuccess = data.every((result) => !('error' in result))

                    if (isSuccess && toastId) {
                        updateToast(toastId, i18n.t('toast.loading.images.success'), 'success')
                    }

                    if (!isSuccess && toastId) {
                        updateToast(toastId, i18n.t('toast.loading.images.error'), 'error')
                    }

                    handleImagesUpdateInvalidation(params)

                    // TODO
                    const module = params[0].module
                    const moduleName = module === 'property' ? 'properties' : 'projects'

                    invalidateQueries([moduleName, 'list'])
                },
                ...options,
            })
        },
    },
    SyncImage: {
        useMutation: (options?: UseMutationOptions<UpdateFileResponse, AxiosError, SyncImageParams>) => {
            return useMutation({
                mutationFn: (params) => syncImage(params.id),
                onSuccess: (data, params) => {
                    const { collection, module, parent } = params

                    invalidateQueries([collection, module, parent])
                },
                onError: (error) => {
                    console.log('Image sync error:', error)
                },
                ...options,
            })
        },
    },
    GetImagesByUrl: {
        useQueries: (images: (Image | { id: number })[], size: 'original' | '') => {
            return useQueries({
                queries: images?.map((image) => {
                    const timestamp = 'updated' in image ? convertDateStringToTimeStamp(image.updated) : null

                    return {
                        queryKey: ['imageUrl', image.id, size, timestamp],
                        queryFn: () => getImageByUrl(image.id, size, timestamp),
                        staleTime: Infinity,
                        enabled: !!image.id,
                    }
                }),
            })
        },
    },
}

export default images
